import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from 'react-i18next';
import { Checkmark, SortingArrows } from "../common/ProjectIcons";
import { ProviderType } from "../common/ObjectTypes";
import NoResults from "../common/NoResults";
import Spinner from "../common/Spinner";
import { Tooltip } from "../common/Tooltip";
import WebhookApiPushModal from "./WebhookAPIPushModal";
import { tableClasses, textClasses, inputClasses } from "../common/tailwindClassnames";

type StepOneProps = {
    providerTypes: ProviderType[],
    reloadProviderTypes: boolean,
    loading: boolean,
    onSelectProviderType: Function,
    onSearchProviderType: Function,
    onPageClickProviderTypes: Function,
    providerTypesTotal: number,
    providerTypesOffset: number,
    pageLoaded: boolean,
    hasMoreData: boolean
}

const NewConnectionStepOne = ({
    providerTypes,
    reloadProviderTypes,
    loading,
    onSelectProviderType,
    onSearchProviderType,
    onPageClickProviderTypes,
    providerTypesTotal,
    providerTypesOffset,
    pageLoaded,
    hasMoreData,
}: StepOneProps) => {
    const { t } = useTranslation();
    const [subStr, setSubStr] = useState("");
    const [selectedType, setSelectedType] = useState<string | null>(null);
    const [showModal, setShowModal] = useState(false);
    const tableContainerRef = useRef<HTMLDivElement | null>(null);

    // Scroll handler moved directly into useEffect

    // Optimize by debouncing the scroll handler
    useEffect(() => {
        const container = tableContainerRef.current;

        // Create a debounced version of the scroll handler
        let scrollTimeout: NodeJS.Timeout;
        const debouncedHandleScroll = () => {
            if (scrollTimeout) clearTimeout(scrollTimeout);
            scrollTimeout = setTimeout(() => {
                if (!tableContainerRef.current || reloadProviderTypes || loading || !hasMoreData) return;

                const { scrollTop, scrollHeight, clientHeight } = tableContainerRef.current;

                // Add a better threshold
                if (scrollTop + clientHeight >= scrollHeight - 50) {
                    onPageClickProviderTypes();
                }
            }, 150);
        };

        if (container) {
            container.addEventListener("scroll", debouncedHandleScroll);
        }

        return () => {
            if (container) {
                container.removeEventListener("scroll", debouncedHandleScroll);
                if (scrollTimeout) clearTimeout(scrollTimeout);
            }
        };
    }, [reloadProviderTypes, providerTypesTotal, loading, hasMoreData, onPageClickProviderTypes]);

    return (
        <div className="mt-8">
            <div className="flex flex-row justify-between items-end">
                {/* div with text and filters */}
                <div className="flex flex-col justify-between w-full mb-5">
                    <div className="flex flex-row items-center mb-2">
                        <label className={textClasses.subheading}>Select the data provider you wish to connect to.</label>
                        <span className="flex items-center translate-y-1 ml-2">
                            <Tooltip
                                description="Start with a generic Webhook or API push, or select from our integrated data providers"
                                position="bottomleft"
                                width="w-96"
                            />
                        </span>
                    </div>
                    <input
                        id="search"
                        placeholder={t("Search provider types") || ""}
                        value={subStr}
                        className={inputClasses.standard + " w-80 mt-4"}
                        onChange={(e) => {
                            onSearchProviderType(e)
                            setSubStr(e.target.value)
                        }}
                    >
                    </input>
                </div>
                {/* text for small and medium screens */}
                <div className="mb-2 ml-4 md:hidden">
                    <p className="text-sm text-medium-gray dark:text-gray-lines mb-2 transition-colors duration-300">Webhooks, API Push, and other sources <button onClick={() => setShowModal(true)} className="text-status-green dark:text-dark-green-100 underline border-none bg-transparent p-0 cursor-pointer transition-colors duration-300">Learn more</button></p>
                </div>
            </div>
            <div className="flex flex-row mb-6">
                {reloadProviderTypes ?
                    <div className="w-full md:w-2/3">
                        <Spinner />
                    </div>
                    :
                    <div className="w-full md:w-2/3">
                        {providerTypes.length < 1 &&
                            <NoResults
                                headerMsg={t("No Provider Types Found")}
                                labelMsg={t("You may need to contact system admin.")}
                            />}
                        {providerTypes.length >= 1 &&
                            <div ref={tableContainerRef} className="w-full h-[450px] overflow-y-scroll border-y border-gray-lines dark:border-secondary-gray scrollbar-thin scrollbar-thumb-neutral-gray-400 dark:scrollbar-thumb-neutral-gray-700 scrollbar-track-transparent hover:scrollbar-thumb-neutral-gray-500 dark:hover:scrollbar-thumb-neutral-gray-600 transition-colors duration-300">
                                <table className={tableClasses.container + " cursor-pointer"}>
                                    <thead className={tableClasses.header + " sticky top-0 z-10 border border-gray-lines dark:border-secondary-gray"}>
                                        <tr>
                                            <th className="w-7"></th>
                                            <th className={tableClasses.headerCell + " w-3/4"}>
                                                {t("Provider")}
                                                <span><SortingArrows /></span>
                                            </th>
                                            <th className={tableClasses.headerCell}>
                                                {t("Help Center")}
                                            </th>
                                        </tr>
                                    </thead>

                                    <tbody className="border border-gray-lines dark:border-secondary-gray dark:text-white transition-colors duration-300">
                                        {providerTypes.map((pt: ProviderType) => {
                                            return (
                                                <tr key={pt.id !== "" ? pt.id : Math.random()} className={`border-b border-gray-lines dark:border-secondary-gray bg-white dark:bg-off-black ${(selectedType === pt.id && pt.id !== "") ? "bg-light-blue dark:bg-neutral-gray-700" : "hover:bg-light-blue dark:hover:bg-neutral-gray-700"} transition-colors duration-300`}
                                                >
                                                    <td className={"p-3 w-7"}>{(selectedType === pt.id && pt.id !== "") ? <Checkmark /> : ""}</td>
                                                    <td className={tableClasses.cell} onClick={() => {
                                                        onSelectProviderType(pt)
                                                        setSelectedType(pt.id)
                                                    }}>
                                                        {pt.name}
                                                    </td>
                                                    {pt.help_center_url ? (
                                                        <td className="p-3 hover:bg-light-blue dark:hover:bg-neutral-gray-700 transition-colors duration-300">
                                                            <a href={pt.help_center_url} target="_blank" rel="noopener noreferrer" className="text-blue-basic dark:text-dark-blue-300 transition-colors duration-300">
                                                                {t("Learn More")}
                                                            </a>
                                                        </td>
                                                    ) : <td className="p-3 hover:bg-light-blue dark:hover:bg-neutral-gray-700 transition-colors duration-300"></td>}
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>

                            </div>
                        }
                        {loading && <div className="relative w-full h-1 bg-gray-200 dark:bg-neutral-gray-700 overflow-hidden mt-2 transition-colors duration-300">
                            <div className="absolute top-0 left-0 w-1/3 h-full bg-dark-green dark:bg-dark-green-100 animate-loading transition-colors duration-300"></div>
                        </div>}
                    </div>}
                <div className="border border-gray-lines dark:border-secondary-gray ml-4 py-4 px-5 w-1/3 h-fit bg-white dark:bg-dark-gray hidden md:block transition-colors duration-300">
                    <WebhookApiPushModal
                        providerTypes={providerTypes}
                        selectedType={selectedType}
                        onSelectProviderType={onSelectProviderType}
                        setSelectedType={setSelectedType}
                        setShowModal={setShowModal}
                    />
                </div>
            </div>

            {/* Modal for Learn More */}
            {showModal && <WebhookApiPushModal
                providerTypes={providerTypes}
                selectedType={selectedType}
                onSelectProviderType={onSelectProviderType}
                setSelectedType={setSelectedType}
                setShowModal={setShowModal}
                isModal={true}
                showModal={showModal}
            />}
        </div>
    );
};

export default NewConnectionStepOne